import React, { useEffect } from 'react';

import './ModalConfirmacao.css';

export interface ModalConfirmacaoProps {
  aberto: boolean;
  titulo: string;
  /** Pergunta principal. */
  mensagem: string;
  /** Linha auxiliar opcional: consequência da ação, contagem afetada, etc. */
  detalhe?: string;
  rotuloConfirmar?: string;
  rotuloCancelar?: string;
  /** Ação destrutiva (remoção/descarte) — pinta o botão de confirmar em vermelho. */
  destrutivo?: boolean;
  /** Trava os botões enquanto a ação está em andamento. */
  processando?: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
}

/**
 * Modal de confirmação do projeto, em substituição ao `window.confirm` (at-24).
 *
 * O `confirm` nativo mostra "localhost:3001 diz", não respeita a identidade visual,
 * bloqueia a thread e não tem como indicar progresso — ruim especialmente no
 * celular, onde a vistoria é feita. Reutilizável: qualquer confirmação do app deve
 * passar por aqui em vez de chamar o nativo.
 */
const ModalConfirmacao: React.FC<ModalConfirmacaoProps> = ({
  aberto,
  titulo,
  mensagem,
  detalhe,
  rotuloConfirmar = 'Confirmar',
  rotuloCancelar = 'Cancelar',
  destrutivo = false,
  processando = false,
  onConfirmar,
  onCancelar,
}) => {
  // Esc cancela — comportamento esperado de diálogo e que o confirm nativo tem.
  useEffect(() => {
    if (!aberto) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !processando) onCancelar();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [aberto, processando, onCancelar]);

  if (!aberto) return null;

  return (
    <div
      className="confirmacao-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      // Clique fora cancela, mas nunca no meio de uma ação em andamento.
      onClick={() => {
        if (!processando) onCancelar();
      }}
    >
      <div className="confirmacao-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirmacao-titulo">{titulo}</div>
        <div className="confirmacao-mensagem">{mensagem}</div>
        {detalhe && <div className="confirmacao-detalhe">{detalhe}</div>}

        <div className="confirmacao-acoes">
          <button
            type="button"
            className="confirmacao-botao confirmacao-cancelar"
            onClick={onCancelar}
            disabled={processando}
          >
            {rotuloCancelar}
          </button>
          <button
            type="button"
            className={`confirmacao-botao ${
              destrutivo ? 'confirmacao-confirmar-destrutivo' : 'confirmacao-confirmar'
            }`}
            onClick={onConfirmar}
            disabled={processando}
          >
            {processando ? 'Aguarde...' : rotuloConfirmar}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalConfirmacao;
